/**
 * Idea impact service.
 *
 * Reads the measured organic-vs-paid impact for an idea from
 * ``GET /api/ideas/{ideaId}/impact``. The backend sums the existing
 * per-post performance read (organic + paid) across the idea's
 * published posts into cumulative totals — this layer only fetches.
 */

import { apiClient } from "@/services/apiClient";
import type { HttpClient } from "@/core/http/types";

/** Total / paid / organic for one additive metric. ``organic = total − paid``. */
export interface ImpactTriple {
  total: number | null;
  paid: number | null;
  organic: number | null;
}

/** Paid-only figures (no organic counterpart). */
export interface ImpactPaid {
  spend: number | null;
  results: number | null;
  clicks: number | null;
}

/** Rolled-up metrics for one platform (or the combined "ALL"). */
export interface ImpactBlock {
  platform: string;
  reach: ImpactTriple;
  impressions: ImpactTriple;
  engagement: ImpactTriple;
  paid: ImpactPaid;
}

export interface IdeaImpact {
  ideaId: string;
  hasData: boolean;
  postsWithData: number;
  publishedPostCount: number;
  platforms: ImpactBlock[];
  combined: ImpactBlock | null;
}

export async function getIdeaImpact(
  http: HttpClient,
  ideaId: string
): Promise<IdeaImpact> {
  try {
    return await http.get<IdeaImpact>(`/api/ideas/${ideaId}/impact`);
  } catch (error) {
    console.error("IdeaImpactService Error: failed to fetch idea impact", error);
    if (error instanceof Error) throw error;
    throw new Error("Failed to fetch idea impact");
  }
}

/** Legacy admin-bound binding for non-React callsites. */
export const ideaImpactService = {
  getIdeaImpact: (ideaId: string) =>
    getIdeaImpact(apiClient as unknown as HttpClient, ideaId),
};
